import Button, {ButtonVariant} from 'bitmovin-web-ui/src/components/core/button';
import baseVariables from 'bitmovin-web-ui/src/styles/baseVariables';
import React from 'react';
import {connect} from 'react-redux';
import HdrConversionImage from 'src/images/HDR_conversion.png';
import styled from 'styled-components';

import AccordionPanel from '../../components/accordionPanel';
import BoundImageModal from '../../components/boundImageModal';
import {Link, pxToEm} from '../../components/globalStyles';
import WrapperAppUl from '../../components/wrapperAppUl';
import HardcodedSalesDemoPath from '../../data/hardcodedSalesDemoPath';
import {AppRoutePathFunction} from '../../logic/routes';
import {RootState} from '../../logic/store';
import {LoadingState, State as SalesDemosState} from '../../logic/store/salesDemosStore';

//#region Styled Components

const StyledImageModal = styled(BoundImageModal)`
  margin-top: ${pxToEm(10)}em;
`;

const StyledImage = styled.img`
  width: 100%;
`;

const StyledWrapperDiv = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  width: 100%;
`;

const StyledAccordionPanel = styled(AccordionPanel)`
  width: 100%;

  :not(:last-child) {
    margin-bottom: ${pxToEm(18)}em;
  }
`;

const StyledButton = styled(Button)`
  margin-top: 1em;
`;

const StyledApiVectorGraphicImgWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
  > img {
    height: ${pxToEm(300)}em;
  }
`;

const StyledUlHeadlineSpan = styled.span`
  display: block;
  font-size: ${pxToEm(30)}em;
  color: ${baseVariables.colors.midnight};
  margin: 0.5em 0;
`;

//#endregion

enum AccordionHeadline {
  Drm = 'DRM',
  StreamingProtocols = 'Streaming Protocols',
  SupportedStorage = 'Supported Storage',
  SupportedConnect = 'Cloud Connect',
  SupportedManaged = 'Supported Bitmovin Managed Cloud Environments\n',
  InputFormats = 'Input Formats',
  InputFileFormats = 'Input File Formats',
  InputAudioCodecsFormats = 'Input Audio Codecs',
  OutputAudioCodecs = 'Output Audio Codecs',
  OutputVideoCodecs = 'Output Video Codecs',
  OutputFileFormat = 'Output File Format',
  Hdr = 'HDR',
  Filters = 'Filters',
  ClosedCaptionsSubtitles = 'Closed Captions & Subtitles'
}

interface MapStateToProps {
  salesDemos?: SalesDemosState;
}

interface Props extends MapStateToProps {
  className?: string;
}

interface State {
  expandedAccordionHeadlines: AccordionHeadline;
}

class CloudEncodingAccordions extends React.Component<Props, State> {
  public state: Readonly<State> = {
    expandedAccordionHeadlines: undefined
  };

  public componentDidMount(): void {
    this.setState({expandedAccordionHeadlines: undefined});
  }

  private handleAccordionPanelExpand = (accordionHeadline: AccordionHeadline) => {
    const isAlreadyExpanded = this.state.expandedAccordionHeadlines === accordionHeadline;
    this.setState({
      expandedAccordionHeadlines: isAlreadyExpanded ? undefined : accordionHeadline
    });
  };

  public render() {
    return (
      <StyledWrapperDiv className={this.props.className}>
        <StyledAccordionPanel
          headline={AccordionHeadline.InputFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputFormats)}>
          <StyledUlHeadlineSpan>Video Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MPEG-1 Video</li>
            <li>MPEG-2 Video</li>
            <li>MPEG-4 Video</li>
            <li>H.261</li>
            <li>H.262</li>
            <li>H.263</li>
            <li>H.264/AVC</li>
            <li>H.265/HEVC</li>
            <li>VP6</li>
            <li>VP8</li>
            <li>VP9</li>
            <li>DNxHD</li>
            <li>Theora</li>
            <li>XDCAM</li>
            <li>XDCAM HD422</li>
            <li>XDCAM IMX</li>
            <li>DV</li>
            <li>DVCPRO</li>
            <li>DVCPROHD</li>
            <li>ProRes</li>
            <li>AVCHD</li>
            <li>AVCIntra</li>
            <li>Cineform HD</li>
            <li>Intel Indeo</li>
            <li>JPEG2000 (J2K)</li>
            <li>WMV</li>
            <li>Dolby Vision</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.InputFileFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputFileFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputFileFormats)}>
          <StyledUlHeadlineSpan>File Formats</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MP4</li>
            <li>MKV</li>
            <li>MOV</li>
            <li>AVI</li>
            <li>FLV</li>
            <li>MPEG2 TS</li>
            <li>MPEG2 PS</li>
            <li>MXF</li>
            <li>LXF</li>
            <li>GXF</li>
            <li>3GP</li>
            <li>WebM</li>
            <li>MPG</li>
            <li>QuickTime</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.InputAudioCodecsFormats}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.InputAudioCodecsFormats}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.InputAudioCodecsFormats)}>
          <StyledUlHeadlineSpan>Audio Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>AAC</li>
            <li>MP3</li>
            <li>AC3 / Dolby Digital</li>
            <li>E-AC3 / Dolby Digital Plus</li>
            <li>FLAC</li>
            <li>Vorbis</li>
            <li>PCM</li>
            <li>WAV</li>
            <li>Dolby Atmos</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Drm}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Drm}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Drm)}>
          <WrapperAppUl>
            <li>Hollywood-/Netflix-grade DRM</li>
            <li>Widevine, PlayReady and Marlin with MPEG-CENC</li>
            <li>FairPlay DRM with HLS</li>
            <li>DASH ClearKey encryption</li>
            <li>SAMPLE-AES and AES-128 encryption for HLS</li>
            <li>Multi-DRM</li>
          </WrapperAppUl>

          {this.renderDemoLinkButton(HardcodedSalesDemoPath.Drm)}
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.ClosedCaptionsSubtitles}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.ClosedCaptionsSubtitles}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.ClosedCaptionsSubtitles)}>
          <WrapperAppUl>
            <li>WebVTT</li>
            <li>CEA-608/708</li>
            <li>SRT</li>
            <li>TTML</li>
            <li>DFXP</li>
            <li>EBU-TT</li>
            <li>SMPTE-TT</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Hdr}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Hdr}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Hdr)}>
          <StyledImageModal
            boundComponent={<StyledImage src={HdrConversionImage} alt="bitmovin 8pp encoding graphic" />}
            source={HdrConversionImage}
          />
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.Filters}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.Filters}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.Filters)}>
          <WrapperAppUl>
            <li>Motion Adaptive Deinterlace</li>
            <li>Denoise</li>
            <li>Crop/Scale/Rotate</li>
            <li>Watermarking</li>
            <li>Audio Mixing</li>
            <li>Unsharp Masking</li>
            <li>Text Overlay</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.StreamingProtocols}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.StreamingProtocols}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.StreamingProtocols)}>
          <WrapperAppUl>
            <li>MPEG-DASH</li>
            <li>Apple HLS</li>
            <li>Progressive MP4</li>
            <li>Smooth Streaming </li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.OutputAudioCodecs}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputAudioCodecs}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputAudioCodecs)}>
          <StyledUlHeadlineSpan>Audio Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>AAC-LC</li>
            <li>HE-AACv1</li>
            <li>HE-AACv2</li>
            <li>MP2</li>
            <li>MP3</li>
            <li>DTS Express, DTS:HD, DTS:X</li>
            <li>Vorbis</li>
            <li>Opus </li>
            <li>Dolby Atmos </li>
            <li>Dolby Digital</li>
            <li>Dolby Digital Plus </li>
          </WrapperAppUl>
        </StyledAccordionPanel>
        <StyledAccordionPanel
          headline={AccordionHeadline.OutputVideoCodecs}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputVideoCodecs}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputVideoCodecs)}>
          <StyledUlHeadlineSpan>Video Codecs</StyledUlHeadlineSpan>
          <WrapperAppUl>
            <li>MPEG-1 Video</li>
            <li>MPEG-2 Video (XDCAM HD 422)</li>
            <li>MPEG-4 Video</li>
            <li>H.264/AVC</li>
            <li>H.265/HEVC</li>
            <li>VP8</li>
            <li>VP9</li>
            <li>AV1</li>
            <li>VVC</li>
          </WrapperAppUl>
        </StyledAccordionPanel>
        <StyledAccordionPanel
          headline={AccordionHeadline.OutputFileFormat}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.OutputFileFormat}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.OutputFileFormat)}>
          <WrapperAppUl>
            <li>MPEG-2 TS</li>
            <li>MP4</li>
            <li>fMP4</li>
            <li>MOV</li>
            <li>WebM</li>
            <li>CMAF</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedStorage}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedStorage}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedStorage)}>
          <WrapperAppUl>
            <li>AWS S3</li>
            <li>Google Cloud Storage</li>
            <li>Azure Blob</li>
            <li>FTP</li>
            <li>SFTP</li>
            <li>HTTP/HTTPs</li>
            <li>Akamai NetStorage</li>
            <li>Aspera</li>
            <li>Scality</li>
            <li>Cloudian HyperStore</li>
            <li>IBM Bluemix Storage</li>
            <li>IBM CleverSafe</li>
            <li>Rackspace Cloud Files</li>
            <li>Openstack Swift</li>
            <li>Apache CloudStack</li>
            <li>Ceph</li>
            <li>Riak</li>
            <li>DELL EMC</li>
            <li>HGST</li>
            <li>HP Helion Eucalyptus</li>
            <li>Oracle Nimbula</li>
            <li>NFS</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedManaged}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedManaged}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedManaged)}>
          <WrapperAppUl>
            <li>Amazon Web Services</li>
            <li>Google Cloud Platform </li>
            <li>Microsoft Azure</li>
          </WrapperAppUl>
        </StyledAccordionPanel>

        <StyledAccordionPanel
          headline={AccordionHeadline.SupportedConnect}
          expanded={this.state.expandedAccordionHeadlines === AccordionHeadline.SupportedConnect}
          onExpand={() => this.handleAccordionPanelExpand(AccordionHeadline.SupportedConnect)}>
          <WrapperAppUl>
            <li>Amazon Web Services</li>
            <li>Google Cloud Platform </li>
            <li>Microsoft Azure</li>
          </WrapperAppUl>
        </StyledAccordionPanel>
      </StyledWrapperDiv>
    );
  }

  public renderDemoLinkButton = (demoPath: string) => {
    const {loadingState, response} = this.props.salesDemos;

    if (loadingState !== LoadingState.Success) {
      return null;
    }

    const allLoadedDemos = response.demos || [];
    const foundDemo = allLoadedDemos.find(salesDemo => salesDemo.path === demoPath);
    if (foundDemo === undefined) {
      return null;
    }

    return (
      <Link to={AppRoutePathFunction.DemoDetail({demoId: foundDemo.id})}>
        <StyledButton variant={ButtonVariant.OutlinedLight}>{foundDemo.name}&nbsp;Demo</StyledButton>
      </Link>
    );
  };
}

const mapStateToProps = (state: RootState): MapStateToProps => ({
  salesDemos: state.salesDemos
});

export default connect(mapStateToProps)(CloudEncodingAccordions);
